import styled from '@emotion/styled'
import { calcRem, breakpoint } from 'component-library/__styling/style-functions'
import { colors, easing, fonts, metrics } from 'component-library/__styling/settings'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'
import { withClass } from 'common/emotion'
import { IconHome } from 'component-library/icons/IconHome/IconHome'
import { IconWestLive, IconWestLiveProps } from 'component-library/icons/IconWestLive/IconWestLive'

import { WebLink } from 'common/navigation/web-link'
import { themedValue } from 'component-library/__styling/themed-value'

export const StyledActionsContainer = styled('div')({
    width: '100%',
    backgroundColor: colors.white,
    borderBottom: `1px solid ${colors.thewest.greyDolphin}`,
})

export const StyledActionsContent = styled('div')({
    display: 'flex',
    alignItems: 'center',
    maxWidth: metrics.thewest.mainContentWidth,
    height: calcRem(40),
    padding: `0 ${calcRem(15)}`,
    margin: '0 auto',
    fontFamily: fonts.thewest.sansSerif,
})

const linkVertPadding = 13

export const headerActionNavTriggerClassName = 'header-action-nav-trigger'
export const StyledNavTrigger = withClass(headerActionNavTriggerClassName)(
    styled('button')({
        marginRight: calcRem(8),
        padding: calcRem(linkVertPadding / 2, 0),
        whiteSpace: 'nowrap',
        border: 0,
        backgroundColor: 'transparent',
        cursor: 'pointer',
        fontSize: calcRem(14),
        fontWeight: 700,
        lineHeight: 1,
        textDecoration: 'none',
        textTransform: 'uppercase',
        transition: `color ${easing.easeOut.fast}, background ${easing.easeOut.fast}, boxShadow ${easing.easeOut.fast}`,
        fontFamily: fonts.thewest.sansSerif,

        '&:hover': {
            textDecoration: 'underline',
        },

        '&:focus, &.is-active': {
            textDecoration: 'underline',
            outline: 'none',
        },
    })
)

export interface StyledSubnavTriggerIconProps {
    isActive?: boolean
}

export const StyledSubnavTriggerIcon = styled('div')<StyledSubnavTriggerIconProps>(
    ({ theme }) => ({
        marginRight: calcRem(8),
        position: 'relative',
        display: 'inline-block',
        verticalAlign: 'middle',
        width: calcRem(18),
        height: calcRem(2),
        background: theme.sectionValues.primaryColor,
        transition: `background ${easing.easeOut.fast}`,
        transform: `translateY(-1px)`,

        '&::before, &::after': {
            position: 'absolute',
            transformOrigin: 'center',
            right: 0,
            left: 0,
            height: calcRem(2),
            background: theme.sectionValues.primaryColor,
            content: `''`,
            transition: `transform ${easing.easeOut.med}, top ${easing.easeOut.fast}, bottom ${easing.easeOut.fast}`,
        },

        '&::before': {
            top: calcRem(5),
        },

        '&::after': {
            bottom: calcRem(5),
        },
    }),
    props =>
        conditionalStyles(props.isActive === true, {
            background: 'transparent',
            '&::before': {
                transform: 'rotate(45deg)',
                top: 0,
            },
            '&::after': {
                transform: 'rotate(-45deg)',
                bottom: 0,
            },
        })
)

export const StyledHomeLink = styled(WebLink)(({ theme }) => ({
    position: 'relative',
    padding: calcRem(0, 8),
    display: 'block',

    '&::before': {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        left: 0,
        height: calcRem(20),
        borderLeft: `1px solid ${colors.thewest.greyDolphin}`,
        content: "''",
    },

    svg: {
        fill: theme.sectionValues.primaryColor,
    },

    '&:hover svg': {
        fill: colors.black,
    },

    '&:focus': {
        outline: 'none',

        svg: {
            fill: colors.thewest.greyKoala,
        },
    },
}))

export const StyledHomeIcon = styled(IconHome)({
    display: 'inline-block',
    width: calcRem(13),
    height: calcRem(13),
})

export const StyledWestLiveIcon = styled(IconWestLive)<IconWestLiveProps>(({ showEqualizer }) => ({
    display: 'inline-block',
    width: showEqualizer ? calcRem(58) : calcRem(38),
    height: calcRem(13),
}))

export const StyledActionGroup = styled('div')({
    display: 'flex',
    marginLeft: 'auto',
    alignItems: 'center',
})
StyledActionGroup.displayName = 'StyledActionGroup'

// Only displayed on smaller screens when form is hidden
export const StyledSearchLink = styled(WebLink)(({ theme }) => ({
    position: 'relative',
    padding: calcRem(0, theme.margins.sm, 0, 0),
    display: 'flex',
    width: calcRem(25),
    height: calcRem(25),
    alignItems: 'center',
    textAlign: 'center',

    '&::after': {
        content: "''",
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        right: 0,
        height: calcRem(20),
        borderLeft: themedValue(theme, {
            thewest: `1px solid ${colors.thewest.greyDolphin}`,
            sevennews: `1px solid ${colors.sevennews.stormGrey}`,
            perthnow: `1px solid ${colors.perthnow.greyMarble}`,
        }),
    },

    [breakpoint('sm')]: {
        display: 'none',
    },
}))
StyledSearchLink.displayName = 'SearchPageLink'
